import { MutationCache, QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query'
import type { createBrowserRouter } from 'react-router-dom'

import { ErroApi } from './client.js'
import { chaves } from './chaves.js'

/**
 * O router é registrado depois da criação do client (dependência circular:
 * o router monta as páginas, que usam o client). Guardá-lo aqui permite
 * navegar de dentro de um handler, onde não há hooks disponíveis.
 */
let router: ReturnType<typeof createBrowserRouter> | null = null

export function registrarRouter(r: NonNullable<typeof router>): void {
  router = r
}

/**
 * Sessão expirada enquanto o painel estava aberto.
 *
 * Três guardas antes de redirecionar — cada uma impede um incômodo real:
 *  1. a própria sonda de sessão devolve 401 quando deslogado; redirecionar
 *     nela criaria laço na tela de login;
 *  2. um 401 vindo da vitrine (visitante comum) não pode mandar ninguém para
 *     o login do admin;
 *  3. já estando no login, não há para onde ir.
 */
function tratarNaoAutorizado(erro: unknown, queryKey?: QueryKey): void {
  if (!(erro instanceof ErroApi) || erro.status !== 401) return

  if (queryKey?.[0] === 'auth') return

  const caminho = window.location.pathname
  if (!caminho.startsWith('/admin')) return
  if (caminho === '/admin/login') return

  // Marca como deslogado sem refetch, e descarta os dados do painel para não
  // ficarem na tela de fundo depois do redirecionamento.
  queryClient.setQueryData(chaves.auth.sessao, null)
  queryClient.removeQueries({ queryKey: chaves.admin.raiz })

  void router?.navigate('/admin/login', {
    replace: true,
    state: { de: caminho + window.location.search, expirou: true },
  })
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      /**
       * Não insiste em erro 4xx: um 404 ou um 422 não melhoram com retry, e
       * repetir só atrasa a mensagem de erro na tela.
       */
      retry: (tentativa, erro) => {
        if (erro instanceof ErroApi && erro.status >= 400 && erro.status < 500) return false
        return tentativa < 2
      },
    },
    mutations: { retry: false },
  },

  // Vale para QUALQUER query ou mutation: uma sessão que expira no meio de um
  // upload é tratada igual a uma que expira numa listagem.
  queryCache: new QueryCache({
    onError: (erro, query) => tratarNaoAutorizado(erro, query.queryKey),
  }),
  mutationCache: new MutationCache({
    onError: (erro) => tratarNaoAutorizado(erro),
  }),
})
